import {
  type AnswerSelectDto,
  AssessmentType,
  type SurveySnapshotChapterSelectDto,
  SurveyStatus,
} from '@@types/api.types';
import { AssessmentSidebar } from '@components/pages/assessment-shell/components/assessment-sidebar.component';
import { setReturnToSearchParam, useSafeReturnTo } from '@hooks/return-to.hook';
import { errorToast } from '@hooks/toast.hook';
import { useGetSurvey, useInnovatorCompleteSurvey } from '@queries/survey.query';
import { useSurveySidebarStore } from '@stores/survey-sidebar.store';
import { getChapterLabel } from '@utils/survey.util';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

type SurveySidebarProps = {
  chapters?: SurveySnapshotChapterSelectDto[];
  isCollapsed?: boolean;
  setIsCollapsed?: (isCollapsed: boolean) => void;
  surveyIsComplete?: boolean;
  technologyId?: string;
};

const buildSurveySidebarChapters = (params: {
  chapters?: SurveySnapshotChapterSelectDto[];
  returnTo: string | null;
  surveyAnswers?: AnswerSelectDto[];
  surveyId?: string;
  technologyId?: string;
}) => {
  const { chapters, returnTo, surveyAnswers, surveyId, technologyId } = params;

  return chapters?.map((chapter) => {
    const chapterSearchParams = new URLSearchParams();
    setReturnToSearchParam({ returnTo, searchParams: chapterSearchParams });
    chapterSearchParams.set('chapterId', chapter.id);

    const sections = chapter.sections.map((section) => {
      const answered =
        surveyAnswers?.filter((answer) => answer.sectionId === section.id && answer.response !== undefined).length ?? 0;

      return {
        answered,
        href: `/technology/${technologyId}/survey/${surveyId}/section/${section.id}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
        id: section.id,
        title: section.name,
        total: section.questions.length,
      };
    });

    return {
      href: `/technology/${technologyId}/survey/${surveyId}?${chapterSearchParams.toString()}`,
      id: chapter.id,
      progress:
        chapter.sections.reduce((accumulator, section) => {
          const answered =
            surveyAnswers?.filter((answer) => answer.sectionId === section.id && answer.response !== undefined)
              .length ?? 0;

          return accumulator + answered / section.questions.length;
        }, 0) / chapter.sections.length,
      sections,
      title: getChapterLabel({ identifier: chapter.identifier, name: chapter.name }),
    };
  });
};

export const SurveySidebar: React.FC<SurveySidebarProps> = (props) => {
  const { chapters, isCollapsed, setIsCollapsed, surveyIsComplete, technologyId } = props;
  const [searchParams] = useSearchParams();
  const { surveyId, sectionId } = useParams();
  const { data: survey } = useGetSurvey(surveyId);
  const { mutateAsync: completeSurvey, isPending: isCompletingSurvey } = useInnovatorCompleteSurvey();
  const navigate = useNavigate();
  const { returnTo } = useSafeReturnTo();
  const { reset } = useSurveySidebarStore();

  const currentChapterId = sectionId
    ? chapters?.find((chapter) => chapter.sections.some((section) => section.id === sectionId))?.id
    : searchParams.get('chapterId') || undefined;
  const allQuestionsAnswered = survey?.progress && survey.progress >= 1;
  const sidebarChapters = buildSurveySidebarChapters({
    chapters,
    returnTo,
    surveyAnswers: survey?.answers,
    surveyId,
    technologyId,
  });

  const onClickSubmit = async () => {
    if (surveyId && technologyId) {
      try {
        const completedSurvey = await completeSurvey({ surveyId });
        reset();

        const assessmentId = completedSurvey?.createdAssessmentId;
        const assessmentType = survey?.assessmentType;

        if (assessmentType === AssessmentType.TRL && assessmentId) {
          navigate(`/technology/${technologyId}?tab=trl&trlTab=individual&assessmentId=${assessmentId}`);
        } else if (assessmentType === AssessmentType.MRL && assessmentId) {
          navigate(`/technology/${technologyId}?tab=mrl&mrlTab=individual&mrlAssessmentId=${assessmentId}`);
        } else {
          const tab = assessmentType?.toLowerCase();
          navigate(`/technology/${technologyId}${tab ? `?tab=${tab}` : ''}`);
        }
      } catch {
        errorToast({ description: 'An error occurred while completing the survey' });
      }
    }
  };

  return (
    <AssessmentSidebar
      chapters={sidebarChapters}
      currentChapterId={currentChapterId}
      currentSectionId={sectionId}
      isCollapsed={isCollapsed}
      isSubmitting={isCompletingSurvey}
      onNavigate={navigate}
      onSubmit={survey?.status === SurveyStatus.DRAFT ? onClickSubmit : undefined}
      setIsCollapsed={setIsCollapsed}
      submitDisabled={!allQuestionsAnswered}
      submitLabel="Submit"
      submitTooltip={allQuestionsAnswered ? undefined : 'Every question must be answered before submitting'}
    />
  );
};
